import { useState, useEffect } from 'react';
import { FileText, Type } from 'lucide-react';

import { saveAs } from 'file-saver';
import { App } from 'antd';

interface TextEditorProps {
  file: File;
  onClose: () => void;
}

export default function TextEditor({ file }: TextEditorProps) {
  const { message } = App.useApp();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  // 读取文件内容
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setContent(e.target?.result as string);
      setLoading(false);
    };
    reader.onerror = () => {
      message.error('文件读取失败');
      setLoading(false);
    };
    reader.readAsText(file);
  }, [file, message]);

  // 导出 TXT 文件
  const exportText = () => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const fileName = file.name.replace(/\.txt$/i, '_edited.txt');
    saveAs(blob, fileName);
    message.success('TXT 文件已下载');
  };

  // 导出 Markdown 文件
  const exportMarkdown = () => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const fileName = file.name.replace(/\.txt$/i, '_edited.md');
    saveAs(blob, fileName);
    message.success('Markdown 文件已下载');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-slate-400">正在加载文件...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 工具栏 */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Type className="w-5 h-5 text-amber-400" />
          </div>
          <span className="text-white font-medium">纯文本编辑</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportText}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 text-gray-300 rounded-xl hover:bg-gray-800 transition-all cursor-pointer border border-gray-700 hover:border-gray-600"
          >
            <FileText className="w-4 h-4" />
            导出 TXT
          </button>
          <button
            onClick={exportMarkdown}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 text-gray-300 rounded-xl hover:bg-gray-800 transition-all cursor-pointer border border-gray-700 hover:border-gray-600"
          >
            <FileText className="w-4 h-4" />
            导出 MD
          </button>
        </div>
      </div>

      {/* 编辑区域 */}
      <div className="flex-1">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full h-full p-5 bg-gray-900 text-gray-200 font-mono text-sm resize-none rounded-xl border border-gray-700 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 transition-colors"
          placeholder="在此编辑简历内容..."
          spellCheck={false}
        />
      </div>

      {/* 底部信息 */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700 text-sm text-gray-500 flex-shrink-0">
        <span>字符数: {content.length}</span>
        <span>行数: {content.split('\n').length}</span>
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}
